// 基于WHO ICOPE知识库的通用建议内容生成器
import { knowledgeBaseService } from './knowledgeBase';

/**
 * 12项通用建议的详细映射配置
 */
const UNIVERSAL_ADVICE_MAPPING = {
  "ICOPE-C3-S3.1.3-Advice-PhysicalActivity": {
    order: 1,
    displayName: "规律身体活动",
    category: "运动健康",
    icon: "🏃‍♀️"
  },
  "ICOPE-C3-S3.1.3-Advice-HealthyDiet": {
    order: 2,
    displayName: "健康饮食",
    category: "营养健康",
    icon: "🥗"
  },
  "ICOPE-C3-S3.1.3-Advice-Hydration": {
    order: 3,
    displayName: "充足水分",
    category: "营养健康",
    icon: "💧"
  },
  "ICOPE-C3-S3.1.3-Advice-OralHealth": {
    order: 4,
    displayName: "口腔健康",
    category: "身体健康",
    icon: "🦷"
  },
  "ICOPE-C3-S3.1.3-Advice-SocialConnection": {
    order: 5,
    displayName: "社交联系",
    category: "心理健康",
    icon: "👥"
  },
  "ICOPE-C3-S3.1.3-Advice-DiseaseManagement": {
    order: 6,
    displayName: "疾病管理",
    category: "身体健康",
    icon: "🏥"
  },
  "ICOPE-C3-S3.1.3-Advice-Vaccination": {
    order: 7,
    displayName: "疫苗接种",
    category: "身体健康",
    icon: "💉"
  },
  "ICOPE-C3-S3.1.3-Advice-MentalHealth": {
    order: 8,
    displayName: "心理健康",
    category: "心理健康",
    icon: "🧠"
  },
  "ICOPE-C3-S3.1.3-Advice-Sleep": {
    order: 9,
    displayName: "优质睡眠",
    category: "生活健康",
    icon: "😴"
  },
  "ICOPE-C3-S3.1.3-Advice-BrainHealth": {
    order: 10,
    displayName: "大脑健康",
    category: "认知健康",
    icon: "🧩"
  },
  "ICOPE-C3-S3.1.3-Advice-EyeHealth": {
    order: 11,
    displayName: "视力健康",
    category: "感官健康",
    icon: "👁️"
  },
  "ICOPE-C3-S3.1.3-Advice-EarHealth": {
    order: 12,
    displayName: "听力健康",
    category: "感官健康",
    icon: "👂"
  }
};

/**
 * 老年友好的内容转化规则
 */
const CONTENT_TRANSFORMATION_RULES = {
  terminology: {
    '身体活动': '适度运动',
    '久坐行为': '长时间坐着不动',
    '静坐行为': '长时间坐着不动',
    '心血管疾病': '心脏和血管疾病',
    '非传染性疾病': '慢性疾病',
    '疫苗接种': '打疫苗',
    '口腔卫生': '口腔清洁',
    '睡眠质量': '睡眠好坏',
    '认知功能': '大脑思维能力',
    '社交参与': '与人交往',
    '水分摄入': '喝水',
    '纤维素': '膳食纤维',
    
    // 目标读者转化：从医护专业人员转为老年人及照护者
    '社区卫生工作者': '社区专业人员',
    '医护人员': '医生或护士',
    '卫生工作者': '医护专业人员',
    '社区工作者': '社区专业人员',
    '照护提供者': '照护人员',
    '护理人员': '照护人员',
    '临床医生': '医生',
    '全科医生': '家庭医生',
    '专科医生': '专业医生',
    '健康专业人员': '医护专业人员'
  },
  
  phrasePatterns: [
    { from: /建议([^。，]+)/, to: '我们建议您$1' },
    { from: /应当([^。，]+)/, to: '最好$1' },
    { from: /有益于([^。，]+)/, to: '对$1有好处' },
    { from: /有助于([^。，]+)/, to: '能够帮助$1' },
    { from: /可以([^。，]+)/, to: '您可以$1' },
    { from: /需要([^。，]+)/, to: '请$1' },
    
    // 针对医护人员语境的转化
    { from: /向老年人提供([^。，]+)/, to: '老年朋友可以获得$1' },
    { from: /为老年人([^。，]+)/, to: '老年朋友$1' },
    { from: /老年人应该([^。，]+)/, to: '建议老年朋友$1' },
    { from: /指导老年人([^。，]+)/, to: '帮助老年朋友$1' },
    { from: /告知老年人([^。，]+)/, to: '让老年朋友了解$1' },
    { from: /评估老年人([^。，]+)/, to: '了解老年朋友$1' },
    { from: /监测老年人([^。，]+)/, to: '关注老年朋友$1' },
    { from: /教育老年人([^。，]+)/, to: '帮助老年朋友学习$1' }
  ]
};

/**
 * 内容转化引擎
 */
class UniversalAdviceTransformationEngine {
  transformForSeniors(content: string): string {
    if (!content) return '';
    
    let transformed = content;
    
    // 替换专业术语
    Object.entries(CONTENT_TRANSFORMATION_RULES.terminology).forEach(([original, friendly]) => {
      const regex = new RegExp(original, 'g');
      transformed = transformed.replace(regex, friendly);
    });
    
    // 调整语气
    CONTENT_TRANSFORMATION_RULES.phrasePatterns.forEach(({ from, to }) => {
      transformed = transformed.replace(from, to);
    });
    
    return transformed.trim();
  }
  
  generateWarmIntro(adviceName: string): string {
    const introTemplates = {
      '规律身体活动': '适度运动对保持身体健康非常重要，即使是简单的活动也很有价值。',
      '健康饮食': '合理的饮食能为身体提供必需的营养，让您保持活力。',
      '充足水分': '保持身体水分充足对健康很重要，特别是对老年朋友来说。',
      '口腔健康': '保持口腔清洁不仅让您感觉舒适，对整体健康也很有帮助。',
      '社交联系': '与家人朋友保持联系能让生活更有意义，心情也会更好。',
      '疾病管理': '合理管理已有的健康问题，能让您的生活质量得到改善。',
      '疫苗接种': '及时打疫苗是保护自己和家人健康的简单有效方法。',
      '心理健康': '关注自己的情绪和心理状态，对整体健康同样重要。',
      '优质睡眠': '良好的睡眠是身体恢复和保持健康的基础。',
      '大脑健康': '保持大脑活跃能帮助您维持思维敏捷和记忆力。',
      '视力健康': '保护眼睛健康能让您更好地享受生活中的美好事物。',
      '听力健康': '保持听力健康有助于与家人朋友更好地交流。'
    };
    
    return introTemplates[adviceName] || `关于${adviceName}，这里有一些实用的建议。`;
  }
}

/**
 * 通用建议内容生成器主类
 */
export class UniversalAdviceContentGenerator {
  private transformEngine: UniversalAdviceTransformationEngine;
  
  constructor() {
    this.transformEngine = new UniversalAdviceTransformationEngine();
  }
  
  /**
   * 生成单个建议的丰富内容
   */
  generateAdviceContent(adviceUID: string, userProfile?: any) {
    const mapping = UNIVERSAL_ADVICE_MAPPING[adviceUID as keyof typeof UNIVERSAL_ADVICE_MAPPING];
    if (!mapping) {
      throw new Error(`未找到建议配置: ${adviceUID}`);
    }
    
    const knowledgeBlock = knowledgeBaseService.getKnowledgeBlock(adviceUID);
    if (!knowledgeBlock) {
      throw new Error(`未找到知识块: ${adviceUID}`);
    }
    
    // 1. 提取和转化核心内容（已包含温馨开场）
    const coreContent = this.extractAndTransformCoreContent(knowledgeBlock);
    
    // 2. 构建延伸阅读内容
    const extendedReading = this.buildExtendedReadingContent(knowledgeBlock);
    
    return {
      id: adviceUID,
      title: knowledgeBlock.title || mapping.displayName,
      displayName: mapping.displayName,
      category: mapping.category,
      icon: mapping.icon,
      order: mapping.order,
      coreContent: coreContent,
      extendedReading: extendedReading,
      adviceNumber: mapping.order
    };
  }
  
  /**
   * 提取和转化核心内容
   */
  private extractAndTransformCoreContent(knowledgeBlock: any): string {
    const uid = knowledgeBlock.uid;
    
    // 为每个建议定制化的丰富内容
    switch (uid) {
      case "ICOPE-C3-S3.1.3-Advice-PhysicalActivity":
        return "任何适度运动都对身体有好处，每一次活动都有价值。您可以从简单的活动开始，比如散步、做家务、园艺或跳舞等您喜欢的活动。重要的是要避免长时间坐着不动，循序渐进地增加活动量。";
        
      case "ICOPE-C3-S3.1.3-Advice-HealthyDiet":
        return "合理的饮食能为身体提供必需的营养，让您保持活力。建议多吃蛋白质食物，如鱼类、豆类、坚果等。每天至少吃5份蔬菜和水果（约400克），少吃盐（每天不超过一茶匙）。与家人朋友一起用餐不仅更有乐趣，也有助于增进食欲。如果大餐有困难，可以选择少食多餐的方式。";
        
      case "ICOPE-C3-S3.1.3-Advice-Hydration":
        return "保持身体水分充足对健康很重要，特别是对老年朋友来说。建议每天喝6-8杯水（约1.6-2升）。天气炎热时要多喝一些。如果有膀胱问题，可以适量减少咖啡和茶的摄入。记住，除非医生特别建议，否则不要限制饮水量。";
        
      case "ICOPE-C3-S3.1.3-Advice-OralHealth":
        return "保持口腔清洁对整体健康很重要。建议每天刷牙，使用含氟牙膏，定期看牙医检查。良好的口腔健康有助于您更好地享受食物，也能预防其他健康问题。";
        
      case "ICOPE-C3-S3.1.3-Advice-SocialConnection":
        return "与家人朋友保持联系能让生活更有意义。您可以经常与他们聊天，参加社区活动，或者加入兴趣小组。即使是简单的问候电话也能带来温暖，让心情更好。";
        
      case "ICOPE-C3-S3.1.3-Advice-DiseaseManagement":
        return "如果您有慢性疾病，合理管理能让生活质量得到改善。按时服药，定期体检，戒烟限酒都是重要的。与医生保持良好沟通，了解自己的病情，学会自我管理。";
        
      case "ICOPE-C3-S3.1.3-Advice-Vaccination":
        return "及时打疫苗是保护自己和家人健康的简单有效方法。特别是流感疫苗和肺炎疫苗对老年朋友很重要。请咨询医生了解哪些疫苗适合您，不要因为担心副作用而拒绝接种。";
        
      case "ICOPE-C3-S3.1.3-Advice-MentalHealth":
        return "关注自己的情绪和心理状态对整体健康同样重要。保持积极的心态，参加让自己开心的活动，与亲友分享感受。如果长期感到低落，不要不好意思寻求专业帮助。";
        
      case "ICOPE-C3-S3.1.3-Advice-Sleep":
        return "良好的睡眠是身体恢复和保持健康的基础。建议每天在固定时间睡觉和起床，创造安静舒适的睡眠环境。如果睡眠有问题，可以尝试睡前放松，避免过度兴奋的活动。";
        
      case "ICOPE-C3-S3.1.3-Advice-BrainHealth":
        return "保持大脑活跃能帮助您维持思维敏捷。建议经常读书看报，玩智力游戏，学习新技能，或者听音乐。这些活动不仅有趣，还能锻炼大脑，延缓认知能力的下降。";
        
      case "ICOPE-C3-S3.1.3-Advice-EyeHealth":
        return "保护眼睛健康能让您更好地享受生活中的美好事物。定期检查视力，及时佩戴合适的眼镜，保持室内光线充足。同时要控制血压和血糖，因为它们也会影响眼睛健康。";
        
      case "ICOPE-C3-S3.1.3-Advice-EarHealth":
        return "保持听力健康有助于与家人朋友更好地交流。避免长时间接触噪音，定期清洁耳朵但不要过度。如果听力下降，不要不好意思使用助听器，它能显著改善生活质量。";
        
      default:
        // 备用方案：从原始内容提取
        const rawContent = knowledgeBlock.content || knowledgeBlock.verbatim_content || '';
        if (!rawContent) return '暂无详细内容';
        
        const keyPoints = this.extractKeyPoints(rawContent);
        const transformedPoints = keyPoints.map(point => 
          this.transformEngine.transformForSeniors(point)
        );
        
        return transformedPoints.join(' ');
    }
  }
  
  /**
   * 提取关键信息点
   */
  private extractKeyPoints(content: string): string[] {
    const points: string[] = [];
    
    // 提取列表项
    const bulletPoints = content.match(/[-•*]\s*([^-•*\n]+)/g) || [];
    bulletPoints.forEach(point => {
      const cleanPoint = point.replace(/[-•*]\s*/, '').trim();
      if (cleanPoint.length > 10) {
        points.push(cleanPoint);
      }
    });
    
    // 如果没有列表项，提取主要句子
    if (points.length === 0) {
      const sentences = content.split(/[。；]/);
      sentences.forEach(sentence => {
        const trimmed = sentence.trim();
        if (trimmed.length > 15 && trimmed.length < 100) {
          points.push(trimmed);
        }
      });
    }
    
    return points.slice(0, 4); // 限制为最多4个要点
  }
  
  /**
   * 提取实用建议
   */
  private extractPracticalTips(knowledgeBlock: any): string[] {
    const tips: string[] = [];
    const content = knowledgeBlock.content || knowledgeBlock.verbatim_content || '';
    
    // 寻找具体的建议和数字
    const tipPatterns = [
      /至少(\d+[^。，]*)/g,
      /每天([^。，]*)/g,
      /建议([^。，]*)/g,
      /可以([^。，]*)/g
    ];
    
    tipPatterns.forEach(pattern => {
      const matches = [...content.matchAll(pattern)];
      matches.forEach(match => {
        if (match[1] && match[1].length < 50) {
          const tip = match[1].trim();
          if (tip.length > 5) {
            tips.push(this.transformEngine.transformForSeniors(tip));
          }
        }
      });
    });
    
    return [...new Set(tips)].slice(0, 3);
  }
  
  /**
   * 构建延伸阅读内容
   */
  private buildExtendedReadingContent(knowledgeBlock: any): any[] {
    const relatedBlocks = knowledgeBaseService.getRelatedBlocks(knowledgeBlock.uid);
    
    return relatedBlocks.map((block: any) => {
      // 增强每个延伸阅读项目的内容
      const originalContent = block.content || block.verbatim_content || '';
      const enhancedContent = this.enhanceExtendedReadingContent(block.uid, originalContent);
      
      return {
        uid: block.uid,
        title: this.transformEngine.transformForSeniors(block.title || '相关信息'),
        content: enhancedContent,
        tags: block.tags,
        source_chapter: block.source_chapter,
        domain: block.domain,
        summary: this.generateContentSummary(enhancedContent)
      };
    }).slice(0, 5);
  }
  
  /**
   * 增强延伸阅读内容
   */
  private enhanceExtendedReadingContent(blockUID: string, originalContent: string): string {
    if (!originalContent) return '暂无详细内容';
    
    // 第一步：基本的老年友好转化
    let enhanced = this.transformEngine.transformForSeniors(originalContent);
    
    // 第二步：深度的目标读者转化
    enhanced = this.deepAudienceTransformation(enhanced);
    
    // 第三步：针对特定类型的内容进行进一步增强
    if (blockUID.includes('LifestyleAdvice') || blockUID.includes('Advice')) {
      // 为生活方式建议添加更多实用信息
      if (enhanced.includes('体力活动') || enhanced.includes('运动')) {
        enhanced += ' 记住，开始运动永远不会太晚，哪怕是每天多走几步路也是好的开始。';
      }
      
      if (enhanced.includes('饮食') || enhanced.includes('营养')) {
        enhanced += ' 如果您对饮食有疑问，建议咨询营养师或医生，制定适合自己的饮食计划。';
      }
      
      if (enhanced.includes('社交') || enhanced.includes('社区')) {
        enhanced += ' 参加社区活动是结识新朋友、保持活力的好方法。';
      }
    }
    
    return enhanced;
  }
  
  /**
   * 深度的目标读者转化
   */
  private deepAudienceTransformation(content: string): string {
    let transformed = content;
    
    // 专业指导语境转化为个人建议语境
    const audienceTransformations = [
      { from: /社区卫生工作者.*?可以.*?建议/g, to: '建议' },
      { from: /社区卫生工作者.*?应该.*?提供/g, to: '您可以获得' },
      { from: /医护人员.*?需要.*?评估/g, to: '需要关注' },
      { from: /专业人员.*?应当.*?指导/g, to: '专业人员会帮助' },
      { from: /卫生工作者.*?可以.*?协助/g, to: '专业人员可以协助' },
      
      // 去除专业评估术语
      { from: /进行.*?评估.*?以确定/g, to: '了解' },
      { from: /实施.*?干预.*?措施/g, to: '采取相应的帮助措施' },
      { from: /制定.*?护理.*?计划/g, to: '制定个人健康计划' },
      { from: /监测.*?治疗.*?效果/g, to: '关注改善情况' },
      
      // 医学术语生活化
      { from: /临床表现/g, to: '症状表现' },
      { from: /病理生理/g, to: '疾病机理' },
      { from: /诊断标准/g, to: '判断依据' },
      { from: /治疗方案/g, to: '改善方法' },
      { from: /预后评估/g, to: '恢复预期' },
      
      // 将第三人称专业指导转为第二人称关怀
      { from: /老年人需要/g, to: '您需要' },
      { from: /老年人应该/g, to: '建议您' },
      { from: /老年人可能/g, to: '您可能会' },
      { from: /老年人往往/g, to: '通常您会' },
      { from: /老年人容易/g, to: '可能容易' }
    ];
    
    audienceTransformations.forEach(({ from, to }) => {
      transformed = transformed.replace(from, to);
    });
    
    // 添加温馨关怀语气
    if (transformed.length > 50) {
      // 如果内容较长，在适当位置添加温馨提示
      if (transformed.includes('建议') && !transformed.includes('请根据')) {
        transformed += ' 请根据您的实际情况，选择适合的建议来实施。';
      }
      
      if (transformed.includes('医生') && !transformed.includes('如有疑问')) {
        transformed += ' 如有疑问，请随时咨询您的医生或护士。';
      }
    }
    
    return transformed.trim();
  }
  
  /**
   * 生成内容摘要
   */
  private generateContentSummary(content: string): string {
    if (!content || content.length < 50) return content;
    
    // 提取前80个字符作为摘要
    const summary = content.substring(0, 80);
    const lastSentenceEnd = Math.max(
      summary.lastIndexOf('。'),
      summary.lastIndexOf('！'),
      summary.lastIndexOf('？')
    );
    
    if (lastSentenceEnd > 20) {
      return summary.substring(0, lastSentenceEnd + 1);
    } else {
      return summary + '...';
    }
  }
  
  /**
   * 组合完整内容（已弃用，直接使用核心内容）
   */
  private combineContent(intro: string, coreContent: string, tips: string[]): string {
    // 直接返回核心内容，避免重复
    return coreContent;
  }
  
  /**
   * 批量生成所有通用建议内容
   */
  generateAllAdviceContent(userProfile?: any) {
    const allAdviceUIDs = Object.keys(UNIVERSAL_ADVICE_MAPPING);
    const results: any[] = [];
    
    allAdviceUIDs.forEach(uid => {
      try {
        const content = this.generateAdviceContent(uid, userProfile);
        results.push(content);
      } catch (error) {
        console.error(`生成建议内容失败 ${uid}:`, error);
        // 提供备用内容
        const mapping = UNIVERSAL_ADVICE_MAPPING[uid as keyof typeof UNIVERSAL_ADVICE_MAPPING];
        results.push({
          id: uid,
          title: mapping.displayName,
          displayName: mapping.displayName,
          category: mapping.category,
          icon: mapping.icon,
          order: mapping.order,
          coreContent: `关于${mapping.displayName}，这是一个重要的健康建议，请咨询专业人员获取更多信息。`,
          extendedReading: [],
          adviceNumber: mapping.order
        });
      }
    });
    
    // 按照顺序排序
    return results.sort((a, b) => a.order - b.order);
  }
}

// 导出单例实例
export const universalAdviceContentGenerator = new UniversalAdviceContentGenerator();